import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type UserRole = "admin" | "pro" | "free";

interface UserPlanData {
  role: UserRole;
  monthlyUsage: number;
  canUseAi: boolean;
  canCreateForm: boolean;
  canExportCsv: boolean;
  isLoading: boolean;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isGift: boolean;
  giftEnd: string | null;
  checkSubscription: () => Promise<void>;

}

const FREE_LIMIT = 50;

export function useUserPlan(): UserPlanData {
  const { user, isAuthenticated, loading: authLoading, session } = useAuth();
  const [role, setRole] = useState<UserRole>("free");
  const [monthlyUsage, setMonthlyUsage] = useState(0);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [giftEnd, setGiftEnd] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);


  const checkSubscription = async () => {
    if (!session?.access_token) return;

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking subscription:", error);
        return;
      }

      if (data?.subscribed && data?.plan === "pro") {
        setRole("pro");
        setSubscriptionEnd(data.subscription_end || null);
        setCancelAtPeriodEnd(data.cancel_at_period_end === true);
      }

    } catch (error) {
      console.error("Error invoking check-subscription:", error);
    }
  };

  useEffect(() => {
    // While auth state is being resolved, keep plan loading
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    // Not authenticated: plan info not applicable
    if (!isAuthenticated || !user?.id) {
      setRole("free");
      setMonthlyUsage(0);
      setSubscriptionEnd(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        // First check local role and usage
        const [{ data: roleData, error: roleErr }, { data: usageData, error: usageErr }] = await Promise.all([
          supabase.rpc("get_user_role", { _user_id: user.id }),
          supabase.rpc("get_monthly_ai_usage", { _user_id: user.id }),
        ]);

        if (roleErr) throw roleErr;
        if (usageErr) throw usageErr;

        if (!cancelled) {
          setRole((roleData as UserRole) || "free");
          setMonthlyUsage(typeof usageData === "number" ? usageData : 0);
        }

        // Check for gift subscription (free Pro month granted by admin)
        const { data: giftSub } = await supabase
          .from("subscriptions")
          .select("status, current_period_end")
          .eq("user_id", user.id)
          .eq("status", "gift")
          .maybeSingle();

        if (!cancelled && giftSub) {
          setIsGift(true);
          setGiftEnd(giftSub.current_period_end || null);
        }

        // Then check Stripe subscription status
        await checkSubscription();
      } catch (error) {
        console.error("Error fetching user plan:", error);
        if (!cancelled) {
          // Fail closed to free + 0 usage, but stop loading
          setRole("free");
          setMonthlyUsage(0);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user?.id, session?.access_token]);

  const isPremium = role === "admin" || role === "pro";
  const isUnderLimit = monthlyUsage < FREE_LIMIT;

  return {
    role,
    monthlyUsage,
    canUseAi: isPremium || isUnderLimit,
    canCreateForm: isPremium || isUnderLimit,
    canExportCsv: isPremium || isUnderLimit,
    isLoading,
    subscriptionEnd,
    cancelAtPeriodEnd,
    isGift,
    giftEnd,


    checkSubscription,
  };
}

export async function incrementAiUsage(formId: string): Promise<void> {
  try {
    // Get form owner
    const { data: form } = await supabase
      .from("forms")
      .select("user_id")
      .eq("id", formId)
      .single();

    if (form?.user_id) {
      await supabase.rpc("increment_ai_usage", { _user_id: form.user_id });
    }
  } catch (error) {
    console.error("Error incrementing AI usage:", error);
  }
}
